import { useRef, type ComponentProps, type ReactNode, type RefObject } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { cn, formatNumber, normalizeLocale } from '../lib/utils';

/**
 * Seeds `AmountInput.value` from a stored number. It has to go through the locale formatter:
 * `String(12.5)` would be read back as `125` where the decimal separator is a comma.
 */
export function amountToDisplay(locale: string, amount: number | undefined): string {
	if (amount == null || amount <= 0) return '';
	return formatNumber(locale, amount, Number.isInteger(amount) ? 0 : 2);
}

type AmountInputProps = Omit<
	ComponentProps<typeof CurrencyInput>,
	| 'value'
	| 'onValueChange'
	| 'intlConfig'
	| 'decimalSeparator'
	| 'groupSeparator'
	| 'suffix'
	| 'prefix'
	| 'className'
	| 'style'
	| 'ref'
> & {
	/** Locale driving the decimal/group separators, e.g. `es` renders `12,50`. */
	locale: string;
	/** Raw display string exactly as typed. Empty string shows the placeholder. */
	value: string;
	/** Called on every keystroke with the raw display string and its parsed number (0 when empty). */
	onValueChange: (display: string, amount: number) => void;
	/** Rendered right after the number, only once something is typed. */
	suffix?: ReactNode;
	/** Values above this are rejected as they are typed. */
	max?: number;
	/** Styles the wrapper, which is the visible box and the click target. */
	className?: string;
	inputClassName?: string;
	ref?: RefObject<HTMLInputElement | null>;
};

/**
 * Locale-aware amount input, extracted from the recurring donation flow.
 *
 * Two things make it behave better than a bare `CurrencyInput`: the caller owns the raw display
 * string (binding `value` to a number breaks decimal commas mid-typing, so the parsed number is
 * handed back separately), and the suffix is real text next to the input instead of a suffix
 * inside it, so the number stays optically centred while it grows.
 */
export function AmountInput({
	locale,
	value,
	onValueChange,
	suffix = '€',
	max = 3000,
	min = 0,
	decimalsLimit = 2,
	allowNegativeValue = false,
	className,
	inputClassName,
	ref,
	...props
}: AmountInputProps) {
	const fallbackRef = useRef<HTMLInputElement>(null);
	const inputRef = ref ?? fallbackRef;
	const hasValue = value !== '';

	return (
		<div
			// The trailing classes are invariants of the box, so they win over whatever the caller
			// passes: it is a text surface, and the gap between number and suffix is the span's margin.
			className={cn('flex items-center justify-center', className, 'cursor-text gap-0 hover:cursor-text')}
			onClick={(event) => {
				// Clicking the padding around the auto-sized input still focuses it.
				if (!(event.target as HTMLElement).closest('input')) inputRef.current?.focus();
			}}
		>
			<CurrencyInput
				ref={inputRef}
				className={cn(
					'min-w-0 bg-transparent text-center text-inherit outline-none placeholder:text-inherit focus:placeholder:text-transparent',
					inputClassName,
				)}
				// Grow with the content so the number plus its suffix stay centred as a unit.
				style={hasValue ? { width: `${Math.max(value.length, 1)}ch` } : undefined}
				value={value}
				intlConfig={{ locale: normalizeLocale(locale) }}
				decimalsLimit={decimalsLimit}
				allowNegativeValue={allowNegativeValue}
				step={1}
				min={min}
				max={max}
				onValueChange={(display, _name, values) => {
					if (max != null && values?.float != null && values.float > max) return;
					onValueChange(display ?? '', values?.float ?? 0);
				}}
				{...props}
			/>
			{hasValue && suffix != null && <span className="pointer-events-none ms-0.5 select-none">{suffix}</span>}
		</div>
	);
}
